import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import { UserService } from '../../../service/user.service';
import { InputFieldComponent } from '../../../../../shared/components/ui/input-field/input-field.component';
import { getTakeUntilDestroyed } from '../../../../../core/helpers/take-until-destroy.helper';

const SUPPORTED_LANGUAGE_CODES = ['en', 'et', 'ru'] as const;

@Component({
  selector: 'app-language-setting',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TranslatePipe,
    InputFieldComponent,
  ],
  templateUrl: './language-setting.component.html',
})
export class LanguageSettingComponent {
  #userService = inject(UserService);
  #translate = inject(TranslateService);
  #takeUntilDestroyed = getTakeUntilDestroyed();

  control = input.required<FormControl<string>>();
  options = signal<Array<{ label: string; value: string }>>([]);

  constructor() {
    effect(() => {
      const ctrl = this.control();
      const settings = this.#userService.settings();
      const lang = settings?.language ?? this.#translate.currentLang ?? 'en';
      ctrl?.setValue(lang, { emitEvent: false });
    });

    this.#translate.onLangChange
      .pipe(startWith(undefined), this.#takeUntilDestroyed())
      .subscribe(() => this.#buildOptions());
    this.#buildOptions();
  }

  #buildOptions(): void {
    this.#translate
      .get('app.components.profileSettings.language')
      .subscribe((langObj) => {
        const opts = SUPPORTED_LANGUAGE_CODES.map((code) => ({
          label: (langObj as Record<string, string>)?.[code] ?? code,
          value: code,
        }));
        this.options.set(opts);
      });
  }
}
